import { ChildProps } from '@apollo/client/react/hoc';
import I18n from 'i18n-js';
import React from 'react';
import ListingsCollection from '@reverbdotcom/commons/src/components/listings_collection';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { withRouter, WithRouterProps } from 'react-router';

import {
  withUserContext,
  IUserContext,
} from '@reverbdotcom/commons/src/components/user_context_provider';

import {
  rowQuery,
  MeRecommendedListings,
  useRecommendedListingsTracking,
  COMPONENT_NAME,
  MIN_COUNT,
} from './recommended_listings_common';
import { userContextIsTrackable } from '@reverbdotcom/commons/src/elog/mparticle';
import { buildMultiClientExperimentGroups } from '@reverbdotcom/commons/src/components/multi_client_experiment_groups';
import { EVERYWHERE_CODE } from '@reverbdotcom/commons/src/constants';

interface ExternalProps {
  limit?: number;
  offset?: number;
  minCount?: number;
  fallbackContent?: JSX.Element;
  handleCreateWatchResult?: () => void;
  showFlags?: boolean;
}

export type IProps = ExternalProps & IUserContext;

export function RecommendedListingsRow(props: ChildProps<IProps, MeRecommendedListings.Query>) {
  const minCount = props.minCount || MIN_COUNT;
  const listings = props.data?.me?.recommendedListings?.listings || [];

  useRecommendedListingsTracking(props.user, props.data?.loading, listings.map(listing => listing.id));
  if (
    !props.data?.loading &&
      listings.length < minCount
  ) {
    if (props.fallbackContent) { return props.fallbackContent; }

    return null;
  }

  return (
    <ListingsCollection
      componentName={COMPONENT_NAME}
      listings={listings}
      loading={props.data?.loading}
      title={I18n.t('discovery.recommendedListings.title')}
      largeTiles={false}
      handleCreateWatchResult={props.handleCreateWatchResult}
      showFlags={props.showFlags}
    />
  );
}

const connect = withGraphql<IProps & WithRouterProps>(
  rowQuery,
  {
    options: (ownProps) => {
      const multiClientExperiments = buildMultiClientExperimentGroups(
        ownProps.user, ownProps.location, { shouldApplyLikelihoodToSellBoosts: true },
      );
      return {
        // Ignore sticky seller location on Homepage.
        context: {
          headers: {
            'X-Item-Region': EVERYWHERE_CODE,
          },
        },
        ssr: false,
        variables: {
          version: 1,
          loggedOut: !!ownProps.user?.loggedOut,
          limit: ownProps.limit,
          offset: ownProps.offset,
          shouldSkipTracking: !userContextIsTrackable(ownProps.user),
          multiClientExperiments: multiClientExperiments,
        },
      };
    },
  },
);

export default withUserContext<IProps>(withRouter(connect(RecommendedListingsRow)));
